import "leaflet/dist/leaflet.css";
import "./styles.css";

import { api, errorText } from "./api";
import { startAddPlace } from "./add-place";
import { byId, debounce } from "./dom";
import { bboxTooLarge, createMap, viewBBox } from "./map";
import { MarkerLayer } from "./markers";
import { openMyRatings } from "./my-ratings";
import { type PlaceContext, openPlaceSheet } from "./place";
import { closeSheet, toast } from "./sheet";
import { loadMe, onMeChange, openProfileSheet } from "./session";

const map = createMap("map");
const markers = new MarkerLayer(map, (id) => selectPlace(id));
const hint = byId("map-hint");

const placeCtx: PlaceContext = {
  onPlaceChanged: (place) => {
    markers.upsert(place);
    markers.select(place.id);
  },
  onClosed: () => markers.select(null),
};

function selectPlace(id: string, focus?: { lat: number; lon: number }): void {
  // openPlaceSheet replaces any open sheet synchronously, and closing one clears
  // the selection — so highlight the marker after it, not before.
  void openPlaceSheet(id, placeCtx);
  markers.select(id);
  if (focus) markers.focus(focus.lat, focus.lon);
}

/** Only the newest viewport request may paint; earlier ones are stale. */
let viewToken = 0;

async function refreshPlaces(): Promise<void> {
  const token = ++viewToken;
  const bbox = viewBBox(map);
  if (bboxTooLarge(bbox)) {
    showHint("Nagyíts rá a térképre, hogy lásd a helyeket.");
    return;
  }
  try {
    const { places } = await api.placesInBBox(bbox);
    if (token !== viewToken) return;
    markers.sync(places);
    if (places.length === 0) showHint("Itt még nincs egy hely sem. Vedd fel te az elsőt!");
    else hideHint();
  } catch (err) {
    if (token !== viewToken) return;
    showHint(errorText(err));
  }
}

function showHint(text: string): void {
  hint.textContent = text;
  hint.hidden = false;
}

function hideHint(): void {
  hint.hidden = true;
}

byId("btn-add").addEventListener("click", () => {
  void startAddPlace(map, {
    onCreated: (place) => {
      closeSheet();
      markers.upsert(place);
      markers.focus(place.lat, place.lon);
      selectPlace(place.id);
      void refreshPlaces();
    },
    openPlace: (id) => selectPlace(id),
  });
});

byId("btn-my-ratings").addEventListener("click", () => {
  void openMyRatings({
    openPlace: (id, lat, lon) => selectPlace(id, { lat, lon }),
  });
});

const profileBtn = byId<HTMLButtonElement>("btn-profile");
profileBtn.addEventListener("click", () => openProfileSheet());
onMeChange((me) => {
  profileBtn.textContent = me ? me.nickname : "Becenév";
  profileBtn.title = me ? "Becenév módosítása" : "Becenév megadása";
});

map.on("moveend", debounce(300, () => void refreshPlaces()));

async function start(): Promise<void> {
  try {
    await loadMe();
  } catch (err) {
    toast(errorText(err), "error");
  }
  await refreshPlaces();
}

void start();
